import {ListItemActionBar} from '@github-ui/list-view/ListItemActionBar'
import {useQueryClient} from '@github-ui/react-query'
import {ArchiveIcon, CopyIcon, DuplicateIcon, SyncIcon, XIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'

import {copyProjectOwnersQueryOptions} from '../../queries/copy-project-owners'
import {MENU_LABELS} from './menu-labels'
import type {Project} from './types'

interface ProjectRowActionMenuProps {
  project: Project
  onCopyProject: (project: Project) => void
  onCopyAsTemplate: (project: Project) => void
  onUnlinkProject?: (project: Project) => void
  onToggleClosed?: (project: Project) => void
  onRemoveFromRecents?: (project: Project) => void
  disabled?: boolean
  className?: string
}

/**
 * Shared project actions. Repository callers supply unlink; account callers supply
 * close/reopen and optional removal from recently viewed.
 */
export function ProjectRowActionMenu({
  project,
  onCopyProject,
  onCopyAsTemplate,
  onUnlinkProject,
  onToggleClosed,
  onRemoveFromRecents,
  disabled = false,
  className,
}: ProjectRowActionMenuProps) {
  const showCopy = project.viewerCanCopy
  const showCopyAsTemplate = project.viewerCanCopyAsTemplate
  const showUnlink = project.viewerCanWrite && !!onUnlinkProject
  const showToggleClosed = project.viewerCanWrite && !!onToggleClosed
  const showDivider = (showCopy || showCopyAsTemplate) && (showUnlink || showToggleClosed || !!onRemoveFromRecents)

  const queryClient = useQueryClient()
  const prefetchOwners = (copyAsTemplate: boolean) => {
    void queryClient.prefetchQuery(copyProjectOwnersQueryOptions({projectId: project.id, copyAsTemplate}))
  }

  return (
    <ListItemActionBar
      label={`Actions for ${project.title}`}
      className={className}
      staticMenuActions={[
        ...(showCopy
          ? [
              {
                key: 'copy',
                render: () => (
                  <ActionList.Item
                    disabled={disabled}
                    onSelect={() => onCopyProject(project)}
                    onMouseEnter={() => prefetchOwners(false)}
                    onFocus={() => prefetchOwners(false)}
                  >
                    <ActionList.LeadingVisual>
                      <CopyIcon aria-hidden="true" />
                    </ActionList.LeadingVisual>
                    {MENU_LABELS.copy}
                  </ActionList.Item>
                ),
              },
            ]
          : []),
        ...(showCopyAsTemplate
          ? [
              {
                key: 'copy-as-template',
                render: () => (
                  <ActionList.Item
                    disabled={disabled}
                    onSelect={() => onCopyAsTemplate(project)}
                    onMouseEnter={() => prefetchOwners(true)}
                    onFocus={() => prefetchOwners(true)}
                  >
                    <ActionList.LeadingVisual>
                      <DuplicateIcon aria-hidden="true" />
                    </ActionList.LeadingVisual>
                    {MENU_LABELS.copyAsTemplate}
                  </ActionList.Item>
                ),
              },
            ]
          : []),
        ...(showDivider
          ? [
              {
                key: 'divider',
                render: () => <ActionList.Divider />,
              },
            ]
          : []),
        ...(showUnlink
          ? [
              {
                key: 'unlink',
                render: () => (
                  <ActionList.Item variant="danger" disabled={disabled} onSelect={() => onUnlinkProject?.(project)}>
                    <ActionList.LeadingVisual>
                      <XIcon aria-hidden="true" />
                    </ActionList.LeadingVisual>
                    {MENU_LABELS.unlink}
                  </ActionList.Item>
                ),
              },
            ]
          : []),
        ...(showToggleClosed
          ? [
              {
                key: 'toggle-closed',
                render: () => (
                  <ActionList.Item disabled={disabled} onSelect={() => onToggleClosed?.(project)}>
                    <ActionList.LeadingVisual>
                      {project.closed ? <SyncIcon aria-hidden="true" /> : <ArchiveIcon aria-hidden="true" />}
                    </ActionList.LeadingVisual>
                    <span>{project.closed ? 'Reopen' : 'Close project'}</span>
                  </ActionList.Item>
                ),
              },
            ]
          : []),
        ...(onRemoveFromRecents
          ? [
              {
                key: 'remove-from-recents',
                render: () => (
                  <ActionList.Item disabled={disabled} onSelect={() => onRemoveFromRecents(project)}>
                    <ActionList.LeadingVisual>
                      <XIcon aria-hidden="true" />
                    </ActionList.LeadingVisual>
                    Remove from recently viewed
                  </ActionList.Item>
                ),
              },
            ]
          : []),
      ]}
    />
  )
}
